import { Component, DestroyRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { BehaviorSubject } from 'rxjs';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { BaseDatasource } from '@shared/base/base.datasource';
import { BaseResponse } from '@shared/base/base-response';
import { TableColumn } from '@shared/interface/table.interface';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ComponentsModule } from '@shared/components/components.module';
import { BottomTableComponent } from '@shared/components/bottom-table/bottom-table.component';
import { CardEmptyComponent } from '@shared/components/card';
import { TableContentComponent } from '@shared/components/table-content/table-content.component';
import { TargetDistributorService } from '../../../target-distributor.service';
import { ITargetDistributorMonitoring__Product } from '../../../target-distributor-monitoring.interface';
import { createMonitoringPaginatorIntl } from '../../monitoring-paginator-intl';
import { MONITORING_PAGINATOR_ROUTE_PROVIDERS, MonitoringPaginatorRoute } from '../../monitoring-paginator-route';

const DEFAULT_PAGE_SIZE = 10;

/**
 * [MAI-2606] Section "Detail Produk" di dalam modal Detail per Distributor.
 *
 * Hasil revisi design review: section ini TIDAK PERNAH disembunyikan penuh. Program
 * Target Seluruh Produk tanpa ketentuan rasio tetap merender section, hanya isinya
 * berupa empty state sampai ada pembelian.
 *
 * Nilai progres dan rasio datang sudah terformat dari backend — tidak ada formatting
 * maupun pengurutan ulang di sisi frontend (endpoint tidak menyediakan param `sort`,
 * urutan abjad nama brand ditentukan backend).
 */
@Component({
  selector: 'app-section-detail-produk',
  standalone: true,
  imports: [CommonModule, MatTableModule, InlineSVGModule, ComponentsModule, BottomTableComponent, CardEmptyComponent, TableContentComponent],
  providers: [
    // Paginator memakai `ActivatedRoute` lokal supaya indeks halamannya tidak ikut
    // query param `page` milik tabel distributor di halaman induk.
    ...MONITORING_PAGINATOR_ROUTE_PROVIDERS,
    // Instance sendiri supaya label tidak bergantung pada `MatPaginatorIntl` global.
    { provide: MatPaginatorIntl, useFactory: createMonitoringPaginatorIntl },
  ],
  templateUrl: './section-detail-produk.component.html',
  styleUrls: ['./section-detail-produk.component.scss'],
})
export class SectionDetailProdukComponent implements OnChanges {
  @Input() targetDistributorId = '';

  /**
   * [AC-02] Subtext "Produk telah ditentukan oleh Program Marketing." dan ikon kuning
   * hanya tampil saat program memang menargetkan produk tertentu — bukan saat section
   * muncul semata karena ketentuan Rasio Pembelian di program Semua Produk.
   *
   * Sumbernya `is_specific_target` pada response detail distributor
   * (GET .../detail/distributor-detail/{targetDistributorId}/{distributorId}).
   */
  @Input() hasSpecificProductTarget = false;

  @Input() distributorId = '';

  readonly STRING_CONSTANTS = STRING_CONSTANTS;
  readonly dataSource = new BaseDatasource<ITargetDistributorMonitoring__Product>();
  readonly tableColumns: TableColumn[] = [
    { key: 'name', title: 'Nama Produk', isSortable: false },
    { key: 'purchase_ratio', title: 'Rasio Pembelian', isSortable: false },
    { key: 'progress', title: 'Progres', isSortable: false },
    { key: 'ratio_status', title: '', isSortable: false },
  ];
  readonly displayedColumns = this.tableColumns.map((column) => column.key);

  keyword = '';
  /**
   * Dilatch true setelah muat pertama. Tidak boleh memakai `dataSource.isFinishLoadingSubject`
   * langsung: subject itu kembali false tiap refetch, sehingga `app-input-search` melepas
   * input-nya dari DOM dan pengguna kehilangan fokus setiap kali mencari.
   */
  readonly isControlsReady = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly targetDistributorService: TargetDistributorService,
    private readonly paginatorRoute: MonitoringPaginatorRoute,
    private readonly destroyRef: DestroyRef
  ) {
    this.dataSource.pageSizeSelect = [10, 25, 50];
    this.dataSource.pageSize = DEFAULT_PAGE_SIZE;
  }

  /**
   * Memuat ulang saat distributor berganti. Dipakai `ngOnChanges`, bukan setter pada
   * `distributorId`, karena Angular mengisi input mengikuti urutan penulisannya di template —
   * dengan setter, fetch bisa berjalan sebelum `targetDistributorId` terisi.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['distributorId'] && !changes['targetDistributorId']) return;
    if (!this.distributorId || !this.targetDistributorId) return;

    this.keyword = '';
    this.goToPage(0);
  }

  get isEmpty(): boolean {
    return this.dataSource.isTableLoaded && !this.dataSource.hasItems;
  }

  /**
   * [AC-04] Dua empty state berbeda, dibedakan dari ada tidaknya kata kunci pencarian.
   * Backend tidak mengirim penanda khusus, jadi kondisi ini diturunkan di frontend.
   */
  get emptyText(): string {
    return this.keyword ? 'Produk tidak ditemukan.' : 'Belum terdapat produk dibeli.';
  }

  /**
   * [MAI-2606 QA] Kolom pencarian disembunyikan saat distributor belum punya produk sama
   * sekali — tidak ada yang bisa dicari (design node 9094-44073).
   *
   * Empty state karena hasil pencarian kosong tetap menampilkan kolomnya: tanpa itu
   * pengguna tidak punya cara menghapus kata kunci dan kembali ke daftar penuh.
   */
  get showSearch(): boolean {
    return !this.isEmpty || !!this.keyword;
  }

  onSearch(keyword: string): void {
    this.keyword = keyword;
    this.goToPage(0);
  }

  onPageChange(source: BaseDatasource<ITargetDistributorMonitoring__Product>): void {
    this.dataSource.pageSize = source.pageSize;
    this.goToPage(source.current_page);
  }

  private goToPage(page: number): void {
    this.dataSource.current_page = page;
    // Paginator membaca halaman dari route lokal ini, bukan dari URL halaman induk.
    this.paginatorRoute.setPage(page);
    this.fetchProducts();
  }

  private fetchProducts(): void {
    this.dataSource.isFinishLoadingSubject.next(false);

    this.targetDistributorService
      .getMonitoringProductList(this.targetDistributorId, this.distributorId, {
        keyword: this.keyword || undefined,
        page: this.dataSource.current_page,
        size: this.dataSource.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.applyProducts(res),
        error: () => this.applyProducts(null),
      });
  }

  private applyProducts(res: BaseResponse<ITargetDistributorMonitoring__Product[]> | null): void {
    const rows = res?.data ?? [];

    this.dataSource.tableSubject.next(rows);
    this.dataSource.totalItem = res?.total_data ?? 0;
    this.dataSource.totalPage = res?.total_page ?? 0;
    this.dataSource.current_page = res?.current_page ?? 0;
    this.dataSource.hasItems = rows.length > 0;
    this.dataSource.isFinishLoadingSubject.next(true);
    this.isControlsReady.next(true);
  }
}
